import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Modal, Switch, TravelInformations } from '@bluevalet/common-ui';
import { getLang } from '@/i18n';
import { persistCart } from '../actions/cart';
import { Trip as TripModel, TripStatus } from '../../booking/model';
import EndStationTripHourPicker from './EndStationTripHourPicker';

import type { FormContent } from './Form';
import { Api } from '../../common/model';

interface Props {
  content: FormContent;
  arrivalSite: Api.Site;
  trip: TripModel;
  slots: Api.SiteSlots | undefined;
  fetchEndTrip(site: number, tripNumber: string | undefined, signal?: AbortSignal): void;
  onChange(content: FormContent): void;
  selectEndTrip(details: object): void;
  forceEndTrip(): void;
  setEndTripUnknown(value: boolean): void;
  clearEndTrip(): void;
  forceValidation: boolean;
}

function EndStationTrip({
  content,
  arrivalSite,
  slots,
  forceValidation,
  onChange,
  trip,
  fetchEndTrip,
  selectEndTrip,
  forceEndTrip,
  setEndTripUnknown,
  clearEndTrip,
}: Props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const refModalTrip = useRef<any>();
  const [tripController, setTripController] = useState(new AbortController());

  const { date, hour, tripNumber, tripDetail } = content;
  const travelInfosValue = trip.status === TripStatus.Unknown ? t('booking-trip_unknown_number_sel') : tripNumber;
  const lang = getLang();

  const onHourChange = (hour: string) => {
    onChange({ ...content, hour });
    dispatch(persistCart());
  };

  const openModal = () => refModalTrip.current?.openModal();

  const hideModal = () => refModalTrip.current?.closeModal();

  const onChangeTripNumber = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    tripController.abort();
    const newController = new AbortController();
    setTripController(newController);

    fetchEndTrip(arrivalSite.id, value, newController.signal);
    onChange({ ...content, tripNumber: value, tripDetail: {} });
  };

  const onClearTripNumber = () => {
    if (trip.status !== TripStatus.Unknown) {
      clearEndTrip();
      onChange({ ...content, tripNumber: '', tripDetail: {} });
    }
  };

  const onSelectItemTrip = (item: any) => {
    if (item !== content.tripDetail) {
      selectEndTrip(item);
      onChange({ ...content, tripNumber: item.number, tripDetail: item });
    }
  };

  const onForceItemTrip = () => {
    forceEndTrip();
    onChange({ ...content, tripNumber: content.tripNumber?.toUpperCase() });
  };

  const onToggleUnknownTrip = () => {
    const isUnknown = trip.status === TripStatus.Unknown;
    const tripNumber = !isUnknown ? t('booking-trip_unknown_number_sel') : '';

    setEndTripUnknown(!isUnknown);

    onChange({ ...content, tripNumber, tripDetail: {} });

    hideModal();
  };

  const getStatus = () => {
    if (trip.isFetchingInformations) return 'loading';
    if (tripNumber !== '' && !trip.isFetchingInformations && trip.results) return 'loaded';
    return '';
  };

  const getInputProps = () => {
    const hasErrors = forceValidation && !tripNumber;

    return {
      'data-cy': `end-trip-informations`,
      disabled: !date || trip.status === TripStatus.Unknown,
      success: trip.status === TripStatus.Found,
      error: hasErrors,
      message: hasErrors ? t('error-required') : '',
      onFocus: () => {
        if (tripNumber !== '' && !trip.isFetchingInformations && trip.status === TripStatus.None) {
          fetchEndTrip(arrivalSite.id, tripNumber);
        }
      },
    };
  };

  return (
    <>
      <div className="Trip">
        <div className="trip-informations">
          <TravelInformations
            type="arrival"
            name="endTripNumber"
            label={`${t(`booking-trip_end_train_number`)} *`}
            site={arrivalSite.name}
            message={t('booking-trip_travel_not_found')}
            value={travelInfosValue}
            onChange={onChangeTripNumber}
            onClearValue={onClearTripNumber}
            onSelectItem={onSelectItemTrip}
            forceSelectItem={onForceItemTrip}
            status={getStatus()}
            results={trip.results}
            inputProps={getInputProps()}
            lang={lang}
          />

          <Switch
            data-cy="unknown-trip-end"
            id="unknown-trip-end"
            name="unknown-trip-end"
            label={t(`booking-trip_unknown_train_number`)}
            onChange={() => {
              if (trip.status !== TripStatus.Unknown) openModal();
              else onToggleUnknownTrip();
            }}
            checked={trip.status === TripStatus.Unknown}
          />
        </div>
        <div className="trip-hour">
          <EndStationTripHourPicker
            date={date}
            hour={hour}
            slots={slots}
            tripDetail={tripDetail}
            forceValidation={forceValidation}
            onHourChange={onHourChange}
          />
        </div>

        <Modal
          id="endTripNumberModal"
          ref={refModalTrip}
          title={t('booking-trip_unknown_modal_title')}
          content={t('booking-trip_unknown_modal_content')}
          primaryText={t('modal_confirm')}
          secondaryText={t('modal_cancel')}
          onClickPrimary={onToggleUnknownTrip}
          onClickSecondary={hideModal}
        />
      </div>
      <div className="ReturnTripNotice">
        <svg>
          <use xlinkHref="#icon__info" />
        </svg>
        <div>{t('booking-return-trip-notice')}</div>
      </div>
    </>
  );
}

export default EndStationTrip;
